import { useCallback, useEffect } from "react";
import type { Track } from "react-native-track-player";
import TrackPlayer, { useProgress } from "react-native-track-player";
import type { Document } from "../types";
import { usePodcastPlayerStore } from "./usePodcastPlayerStore";
import { usePodcasts } from "./usePodcasts";

const usePodcastPlayer = () => {
	const progress = useProgress();
	const isPlaying = usePodcastPlayerStore((store) => store.isPlaying);
	const setIsPlaying = usePodcastPlayerStore((store) => store.setIsPlaying);
	const setCurrentTrackInfo = usePodcastPlayerStore(
		(store) => store.setCurrentTrackInfo,
	);
	const currentTrackInfo = usePodcastPlayerStore(
		(store) => store.currentTrackInfo,
	);
	const setEndOfQueue = usePodcastPlayerStore((store) => store.setEndOfQueue);
	const setIsFirstTrack = usePodcastPlayerStore(
		(store) => store.setIsFirstTrack,
	);
	const setVisible = usePodcastPlayerStore((store) => store.setVisible);
	const { podcastsByType, podcasts } = usePodcasts();
	// biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
	useEffect(() => {
		const checkEndOfQueue = async () => {
			const queue = await TrackPlayer.getQueue();
			const currentTrackIndex = await TrackPlayer.getActiveTrackIndex();
			setEndOfQueue(currentTrackIndex === queue.length - 1);
			setIsFirstTrack(currentTrackIndex === 0);
		};
		checkEndOfQueue();
	}, [currentTrackInfo]);

	const loadTracks = useCallback(
		async ({
			tracks,
			currentPodcastIndex,
			currentTrack,
			shouldReset = true,
		}: {
			tracks: Track[];
			currentPodcastIndex: number;
			currentTrack: Track;
			shouldReset?: boolean;
		}) => {
			const currentQueue = await TrackPlayer.getQueue();
			if (!shouldReset && currentQueue.length > 0) {
				setCurrentTrackInfo(currentTrack);
				await TrackPlayer.skip(currentPodcastIndex);
				return;
			}
			await TrackPlayer.reset();
			await TrackPlayer.add(tracks);
			setCurrentTrackInfo(currentTrack);
			await TrackPlayer.skip(currentPodcastIndex);
		},
		[setCurrentTrackInfo],
	);

	const playTracks = async (tracks: Track[]) => {
		try {
			await TrackPlayer.reset();
			await TrackPlayer.add(tracks);
			await TrackPlayer.play();

			setCurrentTrackInfo(tracks[0]);
			setVisible(true);
			setIsPlaying(true);
		} catch (error) {
			console.error("Error playing tracks:", error);
		}
	};

	const playPause = async () => {
		if (isPlaying) {
			await TrackPlayer.pause();
			setIsPlaying(false);
		} else {
			await TrackPlayer.play();
			setIsPlaying(true);
		}
	};

	const skipToPrevious = async () => {
		await TrackPlayer.skipToPrevious();
		const currentTrackIndex = await TrackPlayer.getActiveTrackIndex();
		if (currentTrackIndex !== undefined) {
			const trackData = await TrackPlayer.getTrack(currentTrackIndex);
			if (trackData) {
				setCurrentTrackInfo(trackData);
			}
			return trackData;
		}
	};

	const skipToNext = async () => {
		await TrackPlayer.skipToNext();
		const currentTrackIndex = await TrackPlayer.getActiveTrackIndex();
		if (currentTrackIndex !== undefined) {
			const trackData = await TrackPlayer.getTrack(currentTrackIndex);
			if (trackData) {
				setCurrentTrackInfo(trackData);
			}
			return trackData;
		}
	};

	const seekTo = async (position: number) => {
		try {
			await TrackPlayer.seekTo(position);
		} catch (error) {
			console.error("Error seeking to position:", error);
		}
	};

	const dismissPlayer = async () => {
		try {
			await TrackPlayer.pause();
			await TrackPlayer.reset();
			setVisible(false);
			setIsPlaying(false);
			setCurrentTrackInfo(null);
		} catch (error) {
			console.error("Error dismissing player:", error);
		}
	};

	const playCustomPodcasts = async (
		podcastId: number,
		customPodcastsIds: number[],
	) => {
		const podcastsToPlay: Document[] = [];
		const startIndex = customPodcastsIds.findIndex((p) => p === podcastId);

		for (let i = startIndex; i < podcasts.length; i++) {
			const podcast = podcasts.find((p) => p.id === customPodcastsIds[i]);
			if (podcast) {
				podcastsToPlay.push(podcast);
			}
		}

		const tracks: Track[] = podcastsToPlay.map((podcast) => ({
			url: podcast.metadata.podcast,
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
		}));

		await playTracks(tracks);
	};

	const handleStartPlay = async (podcastId: number) => {
		const categoryIndex = podcastsByType.findIndex((category) =>
			category.podcasts.some((p) => p.id === Number(podcastId)),
		);

		if (categoryIndex === -1) {
			return;
		}

		const allPodcasts = podcastsByType
			.slice(categoryIndex)
			.flatMap((category) => category.podcasts);

		const podcastIndex = allPodcasts.findIndex(
			(p) => p.id === Number(podcastId),
		);

		const podcastsToPlay =
			podcastIndex !== -1 ? allPodcasts.slice(podcastIndex) : [];

		const tracks: Track[] = podcastsToPlay.map((podcast) => ({
			url: podcast.metadata.podcast,
			title: podcast.content.title,
			artist: podcast.metadata.author,
			artwork: podcast.metadata.thumbnail,
		}));

		await playTracks(tracks);
	};

	return {
		playTracks,
		isPlaying,
		playPause,
		skipToPrevious,
		skipToNext,
		seekTo,
		dismissPlayer,
		progress,
		handleStartPlay,
		playCustomPodcasts,
		loadTracks,
	};
};
export default usePodcastPlayer;
